import React from "react";
import { Card } from "../../../components/shared/Card/Card";
import { useRequiredTenant } from "../../../contexts/TenantContext";
import { formatNumber } from "../../../utils/formatters";

interface PropertyInsightsProps {
  propertyData: {
    propertyInfo: {
      yearBuilt?: number;
      lotSquareFeet?: number;
      livingSquareFeet?: number;
    };
    propertyType?: string;
    ownerOccupied?: boolean;
    floodZone?: boolean;
    floodZoneDescription?: string;
  };
  className?: string;
}

export const PropertyInsights: React.FC<PropertyInsightsProps> = ({
  propertyData,
  className = "",
}) => {
  const { colors } = useRequiredTenant().branding;

  // Calculate property insights
  const insights = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const propertyAge = propertyData.propertyInfo.yearBuilt
      ? currentYear - propertyData.propertyInfo.yearBuilt
      : undefined;

    const isNewConstruction = propertyAge !== undefined && propertyAge <= 5;
    const needsUpdating = propertyAge !== undefined && propertyAge > 30;

    return {
      propertyAge,
      isNewConstruction,
      needsUpdating,
      lotSizeAcres: propertyData.propertyInfo.lotSquareFeet
        ? (propertyData.propertyInfo.lotSquareFeet / 43560).toFixed(2)
        : undefined,
    };
  }, [propertyData]);

  const getAgeStatusStyle = () => {
    if (insights.isNewConstruction) {
      return {
        backgroundColor: `${colors.primary}20`,
        color: colors.primary,
      };
    }
    if (insights.needsUpdating) {
      return {
        backgroundColor: `${colors.accent}20`,
        color: colors.accent,
      };
    }
    return {
      backgroundColor: `${colors.secondary}20`,
      color: colors.secondary,
    };
  };

  return (
    <Card title="Property Insights" className={className} elevation="md">
      <div className="space-y-4">
        {/* Property Age */}
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Property Age</span>
          {insights.propertyAge !== undefined && (
            <span
              className="px-2 py-1 rounded-full text-sm"
              style={getAgeStatusStyle()}
            >
              {insights.propertyAge} years old
            </span>
          )}
        </div>

        {/* Lot Size */}
        <div className="flex items-center justify-between">
          <span style={{ color: `${colors.text}99` }}>Lot Size</span>
          <span className="font-medium">
            {insights.lotSizeAcres ? `${insights.lotSizeAcres} acres` : "N/A"}
          </span>
        </div>

        {/* Property Type */}
        <div className="flex items-center justify-between">
          <span style={{ color: `${colors.text}99` }}>Property Type</span>
          <span className="font-medium">
            {propertyData.propertyType || "N/A"}
          </span>
        </div>

        {/* Living Area */}
        <div className="flex items-center justify-between">
          <span style={{ color: `${colors.text}99` }}>Living Area</span>
          <span className="font-medium">
            {propertyData.propertyInfo.livingSquareFeet
              ? `${formatNumber(
                  propertyData.propertyInfo.livingSquareFeet
                )} sqft`
              : "N/A"}
          </span>
        </div>

        {/* Additional Insights */}
        <div
          className="pt-2 mt-2 border-t"
          style={{ borderColor: colors.primary + "20" }}
        >
          <div className="space-y-2">
            {propertyData.ownerOccupied !== undefined && (
              <div className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: propertyData.ownerOccupied
                      ? colors.primary
                      : `${colors.text}40`,
                  }}
                />
                <span className="text-sm" style={{ color: `${colors.text}99` }}>
                  {propertyData.ownerOccupied
                    ? "Owner Occupied"
                    : "Non-Owner Occupied"}
                </span>
              </div>
            )}
            {propertyData.floodZone && (
              <div className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: colors.accent }}
                />
                <span className="text-sm" style={{ color: `${colors.text}99` }}>
                  Flood Zone: {propertyData.floodZoneDescription || "Yes"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PropertyInsights;
